import React from "react";
import {
  ArrowRight,
  ChartBar,
  CheckCircle,
  Handshake,
  Headset,
  PhoneCall,
  Storefront,
  Truck,
  UsersThree,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import "./rghtnow-pages.css";
import "./services.css";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const serviceCards = [
  {
    image: upload("2026/05/grocery-img-300x300.png"),
    title: "Grocery Delivery",
    text: "Order fresh fruits, vegetables, dairy, beverages, and daily essentials from trusted nearby stores. Fast delivery, great selection.",
  },
  {
    image: upload("2026/05/food-300x300.png"),
    title: "Food Ordering & Delivery",
    text: "Restaurants, cafes, bakeries, and local food outlets delivered hot and fresh to your door.",
  },
  {
    image: upload("2026/05/taxi-300x300.png"),
    title: "Taxi Booking Services",
    text: "Book local taxis and rides for comfortable, on-time travel across your city. Track your ride in real time.",
  },
  {
    image: upload("2026/05/pharmacy-300x300.png"),
    title: "Pharmacy & Healthcare",
    text: "Medicines, wellness products, personal care, and healthcare essentials delivered safely. Prescription upload available.",
  },
  {
    image: upload("2026/05/electronic-300x300.png"),
    title: "Electronics",
    text: "Shop for smartphones, accessories, home appliances, and electronics from verified local sellers.",
  },
  {
    image: upload("2026/05/flower-shop-300x300.png"),
    title: "Flowers & Gifts",
    text: "Send flowers, cakes, bouquets, and gift hampers for birthdays, anniversaries, and special occasions. Same-day delivery available.",
  },
  {
    image: upload("2026/05/pet-shop-300x300.png"),
    title: "Pet Shop",
    text: "Pet food, accessories and more for your furry friends.",
  },
  {
    image: upload("2026/06/mobile-recharge-300x300.png"),
    title: "Mobile Recharge & Utility Bill Payments",
    text: "Pay electricity, water, internet, and gas bills, or recharge mobile numbers - fast, secure, and paperless.",
  },
  {
    image: upload("2026/05/Handy-Man-300x300.png"),
    title: "Handyman",
    text: "Professional home maintenance including electricians, plumbers, AC technicians, cleaning services, and repairs from verified providers.",
  },
  {
    image: upload("2026/05/Local-Shop-300x300.png"),
    title: "Local Store Marketplace",
    text: "Discover and shop from local retail stores across multiple categories, all accessible through one platform.",
  },
  {
    image: upload("2026/05/pet-shop-300x300.png"),
    title: "Multiple On-Demand Services",
    text: "Pet food, accessories, grooming products, and care essentials for your animals delivered to your home.",
  },
  {
    image: upload("2026/06/pest-control-300x300.png"),
    title: "Pest Control",
    text: "Verified pest control professionals for homes, apartments, offices, and commercial properties.",
  },
];

const platformHighlights = [
  { title: "One Platform", text: "Multiple Services in One App", icon: Storefront, tone: "purple" },
  { title: "Trusted Partners", text: "Verified Vendors & Service Providers", icon: Handshake, tone: "blue" },
  { title: "Fast Delivery", text: "Reliable & On-Time Deliveries", icon: Truck, tone: "orange" },
  { title: "Customer Support", text: "Dedicated Support When You Need It", icon: Headset, tone: "green" },
  { title: "Merchant Support", text: "Dedicated Business Growth Support", icon: UsersThree, tone: "pink" },
];

const businessTools = [
  "Online store setup and integration",
  "Marketplace listing and product management",
  "Order and delivery management tools",
  "Digital promotion and marketing support",
  "Customer reach expansion across both markets",
  "AI-powered business analytics",
  "Dedicated onboarding and merchant support team",
];

const commitments = [
  {
    title: "Fast and reliable services",
    text: "Access shopping, deliveries, and professional services from a single platform, saving time and simplifying everyday needs.",
  },
  {
    title: "Secure and smooth operations",
    text: "We help supermarkets, stores, restaurants, and local vendors grow digitally and reach more customers.",
  },
  {
    title: "Support for businesses and communities",
    text: "Supporting local businesses and empowering communities through growth and opportunity.",
  },
  {
    title: "Excellent customer experience",
    text: "Convenient, secure, and hassle-free experiences with a smooth and satisfying customer journey.",
  },
];

const ServicesPage = () => (
    <div className="services-page">
      <RghtnowMarketingHeader active="services" />

      <main>
        <section className="services-hero">
          <div className="container services-hero-grid">
            <div className="services-hero-copy">
              <span className="services-kicker">Services</span>
              <h1>Everything You Need. One <em>Platform.</em></h1>
              <p className="services-lead">One App. Multiple Services. Endless Convenience.</p>
              <p>
                RGHT NOW connects customers, vendors, service professionals, and delivery partners through a single
                marketplace-from groceries to taxis, pharmacy to handyman services. RGHT NOW brings the city to your
                door across the UAE and India.
              </p>
              <div className="services-actions">
                <Link className="services-button services-button-pink" to="/onboard-your-store">
                  Onboard Your Store <ArrowRight size={18} />
                </Link>
                <a className="services-button services-button-line" href="https://rghtnow.com/merchant-acquire-contact-us/" target="_blank" rel="noreferrer">
                  Talk to Our Team <PhoneCall size={17} weight="fill" />
                </a>
              </div>
            </div>
            <div className="services-hero-art">
              <div className="services-hero-shape" />
              <img src={upload("2026/06/Services-1-1024x757.png")} alt="RGHT NOW service marketplace" />
            </div>
          </div>
        </section>

        <section className="services-section services-top">
          <div className="container">
            <div className="services-heading">
              <h2>Our Top <em>Services</em></h2>
              <p>Choose from a wide range of services tailored for your everyday needs.</p>
            </div>
            <div className="services-card-grid">
              {serviceCards.map((service) => (
                <article className="services-card" key={service.title}>
                  <img src={service.image} alt="" />
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="services-proof">
          <div className="container">
            <div className="services-proof-grid">
              {platformHighlights.map((item) => {
                const Icon = item.icon;

                return (
                  <div className={`services-proof-item ${item.tone}`} key={item.title}>
                    <span><Icon size={34} weight="fill" /></span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="services-section services-growth">
          <div className="container services-growth-grid">
            <div>
              <span className="services-kicker">For merchants</span>
              <h2>Built for Business Growth</h2>
              <p>
                Whether you run a small local shop or a multi-branch retail chain, RGHT NOW gives you the tools,
                reach, and support to grow digitally in the UAE and India.
              </p>
              <h3>What we provide:</h3>
              <ul className="services-check-list">
                {businessTools.map((tool) => (
                  <li key={tool}><CheckCircle weight="fill" /> <span>{tool}</span></li>
                ))}
              </ul>
            </div>
            <img src={upload("2026/06/one-platform-1024x597.png")} alt="RGHT NOW platform dashboard" />
          </div>
        </section>

        <section className="services-section services-commitment">
          <div className="container">
            <div className="services-heading">
              <h2>Our Commitment</h2>
              <p>We are committed to making everyday shopping, services, and deliveries simpler.</p>
            </div>
            <div className="services-commitment-grid">
              {commitments.map((item, index) => (
                <article className="services-commitment-card" key={item.title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="services-owner">
          <div className="container services-owner-inner">
            <img src={upload("2026/05/shop-rghtnow.svg")} alt="" />
            <div>
              <span className="services-kicker">Grow with RGHT NOW</span>
              <h2>Are You a Business Owner?</h2>
              <p>
                RGHT-NOW continues to build a smart and connected marketplace ecosystem where customers and businesses
                can grow together.
              </p>
            </div>
            <Link className="services-button services-button-pink" to="/onboard-your-store">
              Onboard Your Store <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        <section className="services-seller-strip">
          <div className="container services-seller-inner">
            <div>
              <h2>Start Selling on RGHT NOW Today!</h2>
              <p>More orders. More customers. More growth.</p>
            </div>
            <div className="services-store-badges">
              <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-1024x303.png")} alt="Google Play" />
              <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-1024x304.webp")} alt="App Store" />
            </div>
            <ChartBar size={50} weight="fill" />
          </div>
        </section>
      </main>

      <RghtnowMarketingFooter />
    </div>
);

export default ServicesPage;
